/**
 * Submit refusal copy for POST /work/gate-submissions/:id/submit.
 *
 * The count is whatever the error body already carries as
 * `unresolvedTaskCount` (or the snake_case alias the plan mapper already
 * accepts). A missing field stays missing — never rendered as 0.
 */

const TRANSPORT_MESSAGE = /^Request failed with status code \d+$/;

function payloadOf(err: unknown): Record<string, unknown> | undefined {
  if (!err || typeof err !== 'object' || !('response' in err)) return undefined;
  const data = (err as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== 'object') return undefined;
  return data as Record<string, unknown>;
}

/** Present only when the payload includes a finite number. */
export function readUnresolvedTaskCount(
  payload: Record<string, unknown> | undefined,
): number | undefined {
  if (!payload) return undefined;
  const camel = payload.unresolvedTaskCount;
  if (typeof camel === 'number' && Number.isFinite(camel)) return camel;
  const snake = payload.unresolved_task_count;
  if (typeof snake === 'number' && Number.isFinite(snake)) return snake;
  return undefined;
}

function readServerMessage(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || TRANSPORT_MESSAGE.test(trimmed) || trimmed === 'An error occurred') {
      return undefined;
    }
    return trimmed;
  }
  if (Array.isArray(value)) {
    const joined = value
      .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
      .join(' ')
      .trim();
    return joined || undefined;
  }
  return undefined;
}

export function formatGateSubmitRefusal(err: unknown): string {
  const payload = payloadOf(err);
  const fromBody = readServerMessage(payload?.message);
  const fromError =
    err && typeof err === 'object' && 'message' in err
      ? readServerMessage((err as { message?: unknown }).message)
      : undefined;
  const server = fromBody || fromError || '';
  const count = readUnresolvedTaskCount(payload);
  if (typeof count !== 'number') return server || 'Submission failed';
  if (server.includes(String(count))) return server;
  return server ? `${server} (${count} unresolved)` : `${count} unresolved`;
}
